import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
);

function QuarterRevenueTrend({
  quarterData,
  weeklyData,
  monthlyData,
  yearlyData,
  onClose,
}) {
  const [view, setView] = useState("yearly");
  const graphs = ["yearly", "quarterly", "monthly", "weekly"];
  const colors = [
    "#1E63E9",
    "#28A745",
    "#DC3545",
    "#FFC107",
    "#6F42C1",
    "#20C997",
    "#FD7E14",
  ];

  let userLoging = {};
  let currencies = [];
  userLoging = JSON.parse(localStorage.getItem("user") || "{}");
  currencies = JSON.parse(localStorage.getItem("currency_rates") || "[]");
  const defaultCurrencyId = Number(userLoging?.default_currency_id || 1);
  const selectedCurrency = currencies.find(
  (currency) =>
    Number(currency.value) === defaultCurrencyId
);
  const symbol = selectedCurrency?.symbol || (defaultCurrencyId === 1 ? "₹" : "");
  const currencyType = defaultCurrencyId === 1 ? "en-IN" : "en-US";
  const amountSuffix = defaultCurrencyId === 1 ? "Cr" : "K";
  const amountDivisor = defaultCurrencyId === 1 ? 10000000 : 1000;

  const [data, setData] = useState({
    labels: [],
    datasets: [],
  });
  const [options, setOptions] = useState({});

  useEffect(() => {
    prepareChart();
  }, [view, quarterData, weeklyData, monthlyData, yearlyData]);

  const prepareChart = () => {
    // ================= YEARLY =================
    if (view === "yearly") {
      const labels = yearlyData.map((item) => item.financial_year);

      setData({
        labels,
        datasets: [
          {
            label: `Yearly Revenue (${amountSuffix})`,
            // YEARLY
            data: yearlyData.map((item) => item.revenue / amountDivisor),
            borderColor: "#1E63E9",
            backgroundColor: "#1E63E9",
            tension: 0.3,
          },
        ],
      });
    }

    // ================= QUARTERLY =================
    if (view === "quarterly") {
      const grouped = {};

      quarterData.forEach((item) => {
        const parts = item.fy_quarter.split("-");

        const year = `${parts[0]}-${parts[1]}`;
        const quarter = parts[2];

        if (!grouped[year]) {
          grouped[year] = {};
        }

        grouped[year][quarter] = item.revenue / amountDivisor;
      });

      const labels = ["Q1", "Q2", "Q3", "Q4"];

      const datasets = Object.keys(grouped).map((year, index) => ({
        label: year,
        data: labels.map((q) => grouped[year][q] || 0),
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        tension: 0.3,
      }));

      setData({
        labels,
        datasets,
      });
    }

    // ================= MONTHLY =================
    if (view === "monthly") {
      const grouped = {};

      monthlyData.forEach((item) => {
        const parts = item.month_label.split("-");

        const year = `${parts[0]}-${parts[1]}`;
        const month = parts[2];

        if (!grouped[year]) {
          grouped[year] = {};
        }

        grouped[year][month] = item.revenue / amountDivisor;
      });

      const labels = [
        "04",
        "05",
        "06",
        "07",
        "08",
        "09",
        "10",
        "11",
        "12",
        "01",
        "02",
        "03",
      ];

      const monthNames = {
        "01": "Jan",
        "02": "Feb",
        "03": "Mar",
        "04": "Apr",
        "05": "May",
        "06": "Jun",
        "07": "Jul",
        "08": "Aug",
        "09": "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
      };

      const datasets = Object.keys(grouped).map((year, index) => ({
        label: year,
        data: labels.map((m) => grouped[year][m] || 0),
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        tension: 0.3,
      }));

      setData({
        labels: labels.map((m) => monthNames[m]),
        datasets,
      });
    }

    // ================= WEEKLY =================
    if (view === "weekly") {
      const grouped = {};

      weeklyData.forEach((item) => {
        if (!grouped[item.fy]) {
          grouped[item.fy] = {};
        }

        // WEEKLY
        grouped[item.fy][item.week_no] = item.weekly_revenue / amountDivisor;
      });

      const labels = Array.from({ length: 53 }, (_, i) => `W${i + 1}`);

      const datasets = Object.keys(grouped).map((fy, index) => ({
        label: fy,
        data: Array.from({ length: 53 }, (_, i) => {
          return grouped[fy][i + 1] || 0;
        }),
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        tension: 0.3,
      }));

      setData({
        labels,
        datasets,
      });
    }

    setOptions({
      responsive: true,
      maintainAspectRatio: false,

      interaction: {
        mode: "nearest",
        intersect: false,
      },

      plugins: {
        legend: {
          position: "top",
        },

        title: {
          display: true,
          text: "Click on legend to deselect",
          padding: {
            top: 10,
            bottom: 10,
          },
          font: {
            size: 12,
            style: "italic",
          },
        },

        datalabels: {
          display: false,
        },

        tooltip: {
          enabled: true,
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: ${symbol}${Number(
                context.parsed.y,
              ).toFixed(2)} ${amountSuffix}`;
            },
          },
        },
      },

      elements: {
        point: {
          radius: 4,
          hoverRadius: 7,
        },

        line: {
          borderWidth: 2,
          tension: 0.3,
        },
      },

      scales: {
        y: {
          ticks: {
            callback: function (value) {
              return `${symbol}${Number(value).toFixed(0)} ${amountSuffix}`;
            },
          },
        },
      },
    });
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title text-capitalize">
              {view} Revenue Trend
            </h5>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            <div className="row">
              <div style={{ marginBottom: 15 }}>
                {graphs.map((type) => (
                  <button
                    key={type}
                    onClick={() => setView(type)}
                    style={{
                      padding: "8px 18px",
                      marginRight: 10,
                      borderRadius: 20,
                      border: "none",
                      background: view === type ? "#1E63E9" : "#ddd",
                      color: view === type ? "#fff" : "#333",
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <div style={{ width: "100%", height: "450px" }}>
                {data?.datasets?.length > 0 && (
                  <Line data={data} options={options} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuarterRevenueTrend;
